import { useEffect } from "react";
import { Link, createFileRoute } from "@tanstack/react-router";
import { ArrowLeft, Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { RequireAuth } from "@/components/RequireAuth";
import { ErrorState } from "@/components/ErrorState";
import { AppShell } from "@/components/layout/AppShell";
import { EmployeeForm } from "@/components/payroll/EmployeeForm";
import { usePayrollEmployee } from "@/lib/api/payroll-employees.queries";
import { useActiveCompany } from "@/lib/company-context";

export const Route = createFileRoute(
  "/companies/$companyId/nomina/empleados/$employeeId",
)({
  component: EmployeeDetailPage,
});

function EmployeeDetailPage() {
  return (
    <RequireAuth>
      <AppShell>
        <EmployeeDetailContent />
      </AppShell>
    </RequireAuth>
  );
}

function EmployeeDetailContent() {
  const { t } = useTranslation();
  const { companyId, employeeId } = Route.useParams();
  const { setActiveCompanyId } = useActiveCompany();

  useEffect(() => {
    if (companyId) setActiveCompanyId(companyId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  const employeeQ = usePayrollEmployee(companyId, employeeId);

  if (employeeQ.isPending) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  }
  if (employeeQ.isError || !employeeQ.data) {
    return (
      <ErrorState
        title={t("payroll.employee.detail.error_title")}
        description={t("payroll.employee.detail.error_body")}
        onRetry={() => void employeeQ.refetch()}
        retryLabel={t("common.retry")}
      />
    );
  }

  const employee = employeeQ.data;
  const fullName = [employee.firstName, employee.otherNames, employee.lastName, employee.secondLastName]
    .filter(Boolean)
    .join(" ");

  return (
    <div className="mx-auto flex max-w-3xl flex-col gap-4">
      <Link
        to="/companies/$companyId/nomina/empleados"
        params={{ companyId }}
        className="inline-flex w-fit items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="h-3 w-3" />
        {t("payroll.employee.detail.back")}
      </Link>
      <h1 className="text-2xl font-semibold tracking-tight">{fullName}</h1>
      <p className="text-sm text-muted-foreground">
        {employee.identificationNumber} • {t(`payroll.contractType.${employee.contractTypeCode}`)}
      </p>

      <EmployeeForm mode={{ kind: "edit", companyId, employee }} />
    </div>
  );
}
